/* eslint-disable react-refresh/only-export-components -- WaymarkProvider + useWaymark hook live together by convention, matching auth/useAuth.tsx */
import React from 'react';
import Waymark from '@waymark/waymark-sdk';
import { apiClient } from '../../../lib/apiClient';
import * as analytics from '../../../lib/analytics';
import { EVENTS } from '../../../lib/analytics';
import type { IssueTokenResponse, WaymarkStatus } from './types';
import { normalizeUrl } from './normalizeUrl';

// Two-stage AI commercial generation, prefilled from the advertiser's website
// URL on step 1 and consumed on step 3. The provider lives ABOVE the wizard
// route so its SDK instance and hidden iframe persist across step navigations
// — the iframe is how the SDK communicates with Waymark, and remounting it
// per step would kill any in-flight topic_search / video_generation.
//
// State diagram (browser-side; webhooks can also promote on the server):
//
//   idle ──startGenerationForUrl──▶ topic_searching ──┬── COMPLETE ─▶ generating
//                                                     └── FAILED   ─▶ topic_failed
//                  generating ──┬── COMPLETE ─▶ ready
//                               └── FAILED   ─▶ generation_failed
//
// If the user edits their website URL and clicks Next again, we orphan the
// old topicId/requestId (no cancel API in the SDK) and start over.
//
// TODO(testing): provider-level state-machine tests are deferred. The
// WaymarkService API layer + normalizeUrl are covered by unit tests, but
// the in-memory state machine (videoCompleted/videoRendered handlers, the
// three-branch resume effect with getVideoData probes, the auto-import
// remount-skip in UploadCreativeStep) currently relies on manual testing.
// Re-evaluate after first prod incidents — mock @waymark/waymark-sdk with
// React Testing Library and assert one test per state transition.

// TODO(pre-prod): these are passed verbatim to sdk.startVideoGeneration
// (verified live in the captured network requests + the SDK's Zod schema),
// but in demo-environment testing Waymark sometimes returns renders that
// don't honor them — we've observed 15s renders against `duration: 30` and
// at least one near-vertical aspect against `aspectRatio: '16:9'`. The
// duration mismatch is open with Waymark support (requestId
// ef3db615-b628-4410-94ec-743dc8fb5785 sent 2026-05-19). Re-test against
// the prod environment and follow up before launch — the SDK exposes no
// other knob to enforce these.
const FIXED_DURATION = 30; // CTV slot
const FIXED_ASPECT_RATIO = '16:9' as const;
const FIXED_LANGUAGE = 'en-US' as const;

interface ProviderState {
  status: WaymarkStatus;
  sourceUrl: string | null;
  videoId: string | null;
  renderedUrl: string | null;
  errorMessage: string | null;
}

interface PersistInput extends Partial<ProviderState> {
  status: WaymarkStatus;
  topicId?: string | null;
  generationRequestId?: string | null;
  errorCode?: string | null;
}

interface WaymarkContextValue {
  status: WaymarkStatus;
  sourceUrl: string | null;
  videoId: string | null;
  renderedUrl: string | null;
  errorMessage: string | null;
  /**
   * True between `videoCompleted` (user clicked Finish in the editor) and
   * `videoRendered` (broadcast-quality file is ready). Lets the UI show a
   * "finalizing your video" indicator during the ~1–2 minute render gap.
   */
  isFinalizing: boolean;
  /** True until the persisted state has been hydrated from the server. */
  isHydrating: boolean;
  /**
   * Kick off (or restart) generation for a given website URL.
   * No-op if the URL matches the in-flight `sourceUrl` and status isn't
   * a terminal-failure. The browser persists progress to the server so
   * a refresh resumes the same run.
   */
  startGenerationForUrl: (url: string) => void;
  /**
   * Open the Waymark editor on the rendered video so the user can fine-tune
   * it. Returns a promise that resolves when the editor UI is up.
   */
  openEditor: () => Promise<void>;
  /** Hide the editor iframe again (returns the user to the wizard step). */
  closeEditor: () => Promise<void>;
  /** True when the editor iframe is mounted and visible. */
  isEditorOpen: boolean;
}

const WaymarkContext = React.createContext<WaymarkContextValue | null>(null);

export function useWaymark(): WaymarkContextValue {
  const ctx = React.useContext(WaymarkContext);
  if (!ctx) throw new Error('useWaymark must be used inside a <WaymarkProvider>');
  return ctx;
}

/**
 * Hydration input from the parent (draft.loaded.waymark). Includes everything
 * in ProviderState plus the `topicId` / `generationRequestId` we need to
 * resume in-flight SDK subscriptions after a refresh.
 */
export interface WaymarkInitial extends ProviderState {
  topicId?: string | null;
  generationRequestId?: string | null;
}

interface WaymarkProviderProps {
  slug: string;
  draftToken: string | null;
  /** Initial state hydrated from the draft (server-side). Null while loading. */
  initial: WaymarkInitial | null;
  children: React.ReactNode;
}

export const WaymarkProvider: React.FC<WaymarkProviderProps> = ({
  slug,
  draftToken,
  initial,
  children,
}) => {
  const [state, setState] = React.useState<ProviderState>({
    status: initial?.status ?? 'idle',
    sourceUrl: initial?.sourceUrl ?? null,
    videoId: initial?.videoId ?? null,
    renderedUrl: initial?.renderedUrl ?? null,
    errorMessage: initial?.errorMessage ?? null,
  });
  const [isEditorOpen, setIsEditorOpen] = React.useState(false);
  const [isFinalizing, setIsFinalizing] = React.useState(false);
  const [isHydrating, setIsHydrating] = React.useState(initial == null);
  // One-shot hydration. The parent wizard rebuilds the fallback `initial`
  // object on every render (for fresh drafts where `draft.loaded?.waymark`
  // is undefined), and the `[initial]` dep below sees a new reference each
  // time. Without this guard, hydration would re-fire on every parent render
  // and reset our in-flight state (status: 'topic_searching' → 'idle'),
  // which then breaks the startGenerationForUrl no-op check and causes a
  // fresh `createTopic` call (whose status polling races and 404s).
  const hasHydratedRef = React.useRef(false);

  // Hydrate when `initial` arrives (the parent waits for the draft load).
  React.useEffect(() => {
    if (hasHydratedRef.current) return;
    if (initial == null) return;
    setState({
      status: initial.status,
      sourceUrl: initial.sourceUrl,
      videoId: initial.videoId,
      renderedUrl: initial.renderedUrl,
      errorMessage: initial.errorMessage,
    });
    // Also sync inflightUrlRef so the no-op check in startGenerationForUrl
    // knows we already have a run going for this URL. Without this, a
    // refresh on /start/video with a persisted `ready` state would re-fire
    // startGenerationForUrl (inflightUrlRef.current is still null from the
    // pre-hydration initializer) and spawn a new topic/video.
    inflightUrlRef.current = initial.sourceUrl;
    setIsHydrating(false);
    hasHydratedRef.current = true;
  }, [initial]);

  const sdkRef = React.useRef<Waymark | null>(null);
  // Promise-based dedupe ref so two concurrent ensureSDK() callers (e.g. under
  // React Strict Mode where effects fire twice in dev) share the same in-flight
  // init rather than each creating a new SDK + calling createAccount twice
  // (the second call would race and either trigger AccountAlreadyExistsError
  // or leave Apollo's internal store in a confused state mid-init).
  const sdkInitPromiseRef = React.useRef<Promise<Waymark | null> | null>(null);
  // The hidden iframe container. Mounted once and lives for the provider's
  // lifetime. We toggle its `display` when opening/closing the editor.
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  // Tracks the URL we're currently generating for. Used to no-op repeat
  // startGenerationForUrl calls and to detect URL changes.
  const inflightUrlRef = React.useRef<string | null>(initial?.sourceUrl ?? null);
  // Each generation run gets a fresh nonce. Subscriptions check the nonce
  // before applying status updates so an orphaned old run can't promote the
  // wizard back into `generating` after the user has restarted.
  const runNonceRef = React.useRef(0);
  // CTV-468: wall-clock of the last generation kickoff, so we can report the
  // generation-start → editor-open gap as a proxy for how long renders take.
  const generationStartedAtRef = React.useRef<number | null>(null);
  const slugRef = React.useRef(slug);
  const draftTokenRef = React.useRef(draftToken);
  slugRef.current = slug;
  draftTokenRef.current = draftToken;

  const persistState = React.useCallback(async (input: PersistInput) => {
    const token = draftTokenRef.current;
    if (!token) return;
    try {
      await apiClient.post('/waymark/state', {
        draftToken: token,
        slug: slugRef.current,
        status: input.status,
        sourceUrl: input.sourceUrl ?? null,
        topicId: input.topicId ?? null,
        generationRequestId: input.generationRequestId ?? null,
        videoId: input.videoId ?? null,
        renderedUrl: input.renderedUrl ?? null,
        errorCode: input.errorCode ?? null,
        errorMessage: input.errorMessage ?? null,
      });
    } catch (e) {

      console.warn('[waymark] persistState failed', e);
    }
  }, []);

  /**
   * Lazily instantiate the SDK + authenticate. Returns the SDK ready to
   * receive method calls. Safe to call multiple times — subsequent calls
   * return the cached instance.
   */
  const ensureSDK = React.useCallback(async (): Promise<Waymark | null> => {
    if (sdkRef.current) return sdkRef.current;
    if (sdkInitPromiseRef.current) return sdkInitPromiseRef.current;
    const token = draftTokenRef.current;
    if (!token) return null;
    if (!containerRef.current) return null;

    const initPromise = (async (): Promise<Waymark | null> => {
      // requestAuth mints a fresh JWT. forceCreate is the recovery flag
      // for "Waymark says no account exists" — our DB might think one was
      // created while the upstream call actually failed.
      const requestAuth = async (forceCreate: boolean): Promise<IssueTokenResponse | null> => {
        try {
          return await apiClient.post<IssueTokenResponse>('/waymark/token', {
            draftToken: token,
            slug: slugRef.current,
            ...(forceCreate ? { forceCreate: true } : {}),
          });
        } catch (e) {

          console.warn('[waymark] token issue failed', e);
          return null;
        }
      };

      const auth = await requestAuth(false);
      if (!auth) return null;

      // CTV-554: the container ref can become null between the entry check
      // above and this point if the wizard body remounts during the await
      // (e.g. seat switch via "Advertise with another business"). The SDK
      // constructor throws InvalidParameterError on a null domElement, so
      // bail cleanly — the next identity's WaymarkProvider mount will
      // re-issue the token and re-init against the fresh container.
      if (!containerRef.current) {
        console.info('[waymark] container unmounted during init — aborting');
        return null;
      }

      // Constructor with `domElement` already appends the iframe and starts
      // loading the SDK page — no separate `mount()` call is needed (verified
      // in @waymark/waymark-sdk@4.1.0 source: index.mjs line 638). Calling
      // mount() with the same element is a no-op, but explicit clarity here.
      const sdk = new Waymark(auth.partnerID, {
        authMode: 'jwt',
        environment: auth.environment,
        domElement: containerRef.current,
      });

      const authenticate = async (a: IssueTokenResponse): Promise<void> => {
        if (a.mode === 'create') {
          await sdk.createAccount(a.token);
        } else {
          await sdk.loginAccount(a.token);
        }
      };

      try {
        await authenticate(auth);
      } catch (e) {
        // If loginAccount failed because Waymark has no record of the
        // account (our DB marked account_created_at but the upstream
        // createAccount actually failed earlier — most often during the
        // dotenv-$ secret-mangling era), recover by forcing a fresh
        // createAccount round-trip.
        const msg = e instanceof Error ? e.message : '';
        const looksLikeNoAccount =
          auth.mode === 'login' && /No account found/i.test(msg);
        if (!looksLikeNoAccount) {
          console.warn('[waymark] auth failed', e);
          try { await sdk.cleanup(); } catch { /* noop */ }
          return null;
        }
        console.info('[waymark] login failed with "No account found" — retrying as create');
        const retry = await requestAuth(true);
        if (!retry || retry.mode !== 'create') {
          console.warn('[waymark] forceCreate retry did not return a create token');
          try { await sdk.cleanup(); } catch { /* noop */ }
          return null;
        }
        try {
          await authenticate(retry);
        } catch (e2) {

          console.warn('[waymark] auth failed (forceCreate retry)', e2);
          try { await sdk.cleanup(); } catch { /* noop */ }
          return null;
        }
      }

      return sdk;
    })();
    sdkInitPromiseRef.current = initPromise;

    const sdk = await initPromise;
    if (!sdk) {
      sdkInitPromiseRef.current = null;
      return null;
    }

    sdk.on('videoCompleted', (videoData) => {
      // Fires when the user clicks Finish inside the editor — broadcast-quality
      // rendering ALWAYS begins regardless of whether the video had a previous
      // successful render. `videoData.renders` may still include stale entries
      // from earlier Finish clicks, so we don't use them to decide whether to
      // show the spinner. Instead: always flip `isFinalizing` true, and clear
      // any prior `renderedUrl` (it's stale until the new render lands via the
      // `videoRendered` event). State stays at `ready` — AI generation isn't
      // being redone, just the final render.
      analytics.track(EVENTS.WAYMARK_VIDEO_COMPLETED);
      setIsFinalizing(true);
      setState((s) => ({
        ...s,
        status: 'ready',
        videoId: videoData.id,
        renderedUrl: null,
        errorMessage: null,
      }));
      void persistState({
        status: 'ready',
        sourceUrl: inflightUrlRef.current,
        videoId: videoData.id,
        renderedUrl: null,
      });
    });

    sdk.on('videoRendered', (videoData) => {
      // `videoRendered` can fire more than once — Waymark renders both
      // `email` and `broadcast_quality` formats, and we've seen the event
      // emit for the email format before broadcast_quality is ready. If we
      // clear `isFinalizing` on that first emission, the UI briefly drops
      // back to the "click Customize" placeholder before the real render
      // lands. Ignore fires that don't include the broadcast_quality URL.
      const renders = videoData.renders ?? [];
      const rendered = renders.find((r) => r.status === 'succeeded' && r.format === 'broadcast_quality');
      if (!rendered?.url) return;
      analytics.track(EVENTS.WAYMARK_VIDEO_RENDERED);
      setIsFinalizing(false);
      const next: ProviderState = {
        status: 'ready',
        sourceUrl: inflightUrlRef.current,
        videoId: videoData.id,
        renderedUrl: rendered.url,
        errorMessage: null,
      };
      setState(next);
      void persistState({
        status: next.status,
        sourceUrl: next.sourceUrl,
        videoId: next.videoId,
        renderedUrl: next.renderedUrl,
      });
    });

    // editorExited fires for BOTH "Exit" and "Finish" inside the editor.
    // Either way, the editor view inside Waymark's iframe has closed and
    // its content goes blank — so we need to shrink our fullscreen overlay
    // back to its hidden 1×1 size or the user is left staring at white.
    sdk.on('editorExited', () => {
      analytics.track(EVENTS.WAYMARK_EDITOR_EXITED);
      setIsEditorOpen(false);
    });

    sdkRef.current = sdk;
    return sdk;
  }, [persistState]);

  // ---- Subscription helpers ----
  // Extracted so both initial start and resume-after-hydration can reuse the
  // same status-handling logic. Both helpers re-establish the SDK subscription
  // for an existing topicId / requestId — they do NOT issue create calls.

  const attachGenerationStatusSub = React.useCallback(async (
    sdk: Waymark,
    args: { requestId: string; videoId: string; sourceUrl: string; topicId: string; nonce: number },
  ) => {
    const { requestId, videoId, sourceUrl, topicId, nonce } = args;
    await sdk.subscribeToGenerationStatusUpdates(requestId, (genUpdate) => {
      if (runNonceRef.current !== nonce) return;
      if (genUpdate.status === 'FAILED') {
        const msg = genUpdate.error?.message ?? 'Video generation failed';
        analytics.track(EVENTS.WAYMARK_GENERATION_FAILED, { code: genUpdate.error?.code ?? null });
        setState((s) => ({ ...s, status: 'generation_failed', errorMessage: msg }));
        void persistState({
          status: 'generation_failed',
          sourceUrl,
          topicId,
          generationRequestId: requestId,
          videoId,
          errorCode: genUpdate.error?.code ?? null,
          errorMessage: msg,
        });
        return;
      }
      if (genUpdate.status === 'COMPLETE') {
        // AI generation finished — the video exists as a DRAFT on Waymark's
        // side. We DON'T get a programmatic preview URL: the SDK's Zod schema
        // strips `previewUrl` from startVideoGeneration's response, and
        // `getVideoData` returns `renders: []` for un-Finished videos
        // (verified 2026-05-18). The actual preview lives inside Waymark's
        // editor iframe, which the user opens via Customize. The
        // broadcast-quality `renderedUrl` only populates after they click
        // Finish in the editor → `videoRendered` event fires.
        analytics.track(EVENTS.WAYMARK_READY);
        setState((s) => ({ ...s, status: 'ready' }));
        void persistState({
          status: 'ready',
          sourceUrl,
          topicId,
          generationRequestId: requestId,
          videoId,
        });
      }
    });
  }, [persistState]);

  const attachTopicSearchStatusSub = React.useCallback(async (
    sdk: Waymark,
    args: { topicId: string; sourceUrl: string; nonce: number },
  ) => {
    const { topicId, sourceUrl, nonce } = args;
    await sdk.subscribeToTopicSearchStatusUpdates(topicId, async (update) => {
      if (runNonceRef.current !== nonce) return;
      if (update.status === 'FAILED') {
        const msg = update.error?.message ?? 'Topic search failed';
        analytics.track(EVENTS.WAYMARK_TOPIC_FAILED, { code: update.error?.code ?? null });
        setState((s) => ({ ...s, status: 'topic_failed', errorMessage: msg }));
        void persistState({
          status: 'topic_failed',
          sourceUrl,
          topicId,
          errorCode: update.error?.code ?? null,
          errorMessage: msg,
        });
        return;
      }
      if (update.status !== 'COMPLETE') return;

      let requestId: string;
      let videoId: string;
      try {
        const gen = await sdk.startVideoGeneration({
          topicId,
          aspectRatio: FIXED_ASPECT_RATIO,
          duration: FIXED_DURATION,
          language: FIXED_LANGUAGE,
        });
        requestId = gen.requestId;
        videoId = gen.videoId;
      } catch (e: unknown) {
        if (runNonceRef.current !== nonce) return;
        const msg = e instanceof Error ? e.message : 'Video generation failed to start';
        setState((s) => ({ ...s, status: 'generation_failed', errorMessage: msg }));
        void persistState({
          status: 'generation_failed',
          sourceUrl,
          topicId,
          errorMessage: msg,
        });
        return;
      }
      if (runNonceRef.current !== nonce) return;

      setState((s) => ({ ...s, status: 'generating', videoId }));
      void persistState({
        status: 'generating',
        sourceUrl,
        topicId,
        generationRequestId: requestId,
        videoId,
      });

      await attachGenerationStatusSub(sdk, { requestId, videoId, sourceUrl, topicId, nonce });
    });
  }, [persistState, attachGenerationStatusSub]);

  const startGenerationForUrl = React.useCallback((url: string) => {
    const trimmed = normalizeUrl(url);
    if (!trimmed) return;
    // No-op if we're already running against this URL and not in a failure
    // state. Lets the wizard call this on every Next click without worrying
    // about wasted Waymark credits.
    if (
      inflightUrlRef.current === trimmed &&
      (state.status === 'topic_searching' ||
        state.status === 'generating' ||
        state.status === 'ready')
    ) {
      return;
    }

    inflightUrlRef.current = trimmed;
    const myNonce = ++runNonceRef.current;
    generationStartedAtRef.current = Date.now();
    analytics.track(EVENTS.WAYMARK_GENERATION_STARTED);

    setState({
      status: 'topic_searching',
      sourceUrl: trimmed,
      videoId: null,
      renderedUrl: null,
      errorMessage: null,
    });
    // Clear the prior IDs server-side too so a webhook for the old run
    // can't promote us out of the new one.
    void persistState({
      status: 'topic_searching',
      sourceUrl: trimmed,
      topicId: null,
      generationRequestId: null,
      videoId: null,
      renderedUrl: null,
      errorCode: null,
      errorMessage: null,
    });

    (async () => {
      const sdk = await ensureSDK();
      if (!sdk || runNonceRef.current !== myNonce) return;

      let topicId: string;
      try {
        const result = await sdk.startTopicSearch(trimmed);
        topicId = result.topicId;
      } catch (e: unknown) {
        if (runNonceRef.current !== myNonce) return;
        const msg = e instanceof Error ? e.message : 'Topic search failed to start';
        setState((s) => ({ ...s, status: 'topic_failed', errorMessage: msg }));
        void persistState({ status: 'topic_failed', sourceUrl: trimmed, errorMessage: msg });
        return;
      }

      void persistState({ status: 'topic_searching', sourceUrl: trimmed, topicId });

      await attachTopicSearchStatusSub(sdk, { topicId, sourceUrl: trimmed, nonce: myNonce });
    })();
  }, [ensureSDK, persistState, state.status, attachTopicSearchStatusSub]);

  // Resume in-flight subscriptions after page refresh. If state was persisted
  // as `topic_searching` or `generating`, the SDK from the previous session is
  // long gone — we need to re-attach to the existing topicId / requestId so
  // polling continues, otherwise the wizard sits forever on "generating" with
  // no way to learn the run finished. One-shot via ref to avoid re-resuming
  // on every status flip.
  const hasResumedRef = React.useRef(false);
  React.useEffect(() => {
    if (hasResumedRef.current) return;
    if (!hasHydratedRef.current) return;
    if (!initial) return;
    if (!draftToken) return;
    const sourceUrl = initial.sourceUrl;
    if (!sourceUrl) return;
    if (initial.status === 'topic_searching' && initial.topicId) {
      hasResumedRef.current = true;
      const nonce = ++runNonceRef.current;
      inflightUrlRef.current = sourceUrl;
      console.info('[waymark.resume] topic_searching', { topicId: initial.topicId, sourceUrl });
      void (async () => {
        const sdk = await ensureSDK();
        if (!sdk || runNonceRef.current !== nonce) return;
        await attachTopicSearchStatusSub(sdk, { topicId: initial.topicId!, sourceUrl, nonce });
      })();
    } else if (
      initial.status === 'generating' &&
      initial.generationRequestId &&
      initial.videoId &&
      initial.topicId
    ) {
      hasResumedRef.current = true;
      const nonce = ++runNonceRef.current;
      inflightUrlRef.current = sourceUrl;
      console.info('[waymark.resume] generating', {
        requestId: initial.generationRequestId,
        videoId: initial.videoId,
        sourceUrl,
      });
      void (async () => {
        const sdk = await ensureSDK();
        if (!sdk || runNonceRef.current !== nonce) return;
        // Belt-and-suspenders: probe getVideoData before re-subscribing.
        // If the video already has a successful broadcast_quality render
        // (e.g. user Finished in a prior session and webhook didn't promote
        // server state, or Waymark's subscribe doesn't re-emit terminal
        // status for old requests), short-circuit to 'ready' instead of
        // waiting indefinitely on a subscription that won't fire.
        try {
          const videoData = await sdk.getVideoData(initial.videoId!);
          if (runNonceRef.current !== nonce) return;
          const rendered = (videoData?.renders ?? []).find(
            (r) => r.status === 'succeeded' && r.format === 'broadcast_quality',
          );
          if (rendered?.url) {
            console.info('[waymark.resume] short-circuit: video already rendered', {
              videoId: initial.videoId,
              url: rendered.url,
            });
            setState((s) => ({
              ...s,
              status: 'ready',
              videoId: initial.videoId!,
              renderedUrl: rendered.url,
              errorMessage: null,
            }));
            void persistState({
              status: 'ready',
              sourceUrl,
              videoId: initial.videoId!,
              renderedUrl: rendered.url,
            });
            return;
          }
        } catch (e) {
          console.warn('[waymark.resume] getVideoData probe failed', e);
        }
        if (runNonceRef.current !== nonce) return;
        await attachGenerationStatusSub(sdk, {
          requestId: initial.generationRequestId!,
          videoId: initial.videoId!,
          sourceUrl,
          topicId: initial.topicId!,
          nonce,
        });
      })();
    } else if (initial.status === 'ready' && initial.videoId && !initial.renderedUrl) {
      // User clicked Finish in the editor (which persisted status='ready'
      // with renderedUrl=null) and then left the wizard entirely before
      // `videoRendered` fired. The SDK subscription died with the unmount,
      // so we never picked up the broadcast-quality URL. Probe getVideoData
      // on remount — if the render is sitting on Waymark's side, populate
      // renderedUrl so the auto-import picks it up.
      hasResumedRef.current = true;
      const nonce = ++runNonceRef.current;
      inflightUrlRef.current = sourceUrl;
      console.info('[waymark.resume] ready, probing for missing renderedUrl', {
        videoId: initial.videoId,
      });
      void (async () => {
        const sdk = await ensureSDK();
        if (!sdk || runNonceRef.current !== nonce) return;
        try {
          const videoData = await sdk.getVideoData(initial.videoId!);
          if (runNonceRef.current !== nonce) return;
          const rendered = (videoData?.renders ?? []).find(
            (r) => r.status === 'succeeded' && r.format === 'broadcast_quality',
          );
          if (rendered?.url) {
            console.info('[waymark.resume] ready: recovered renderedUrl', {
              videoId: initial.videoId,
              url: rendered.url,
            });
            setState((s) => ({ ...s, renderedUrl: rendered.url }));
            void persistState({
              status: 'ready',
              sourceUrl,
              videoId: initial.videoId!,
              renderedUrl: rendered.url,
            });
          }
        } catch (e) {
          console.warn('[waymark.resume] ready: getVideoData probe failed', e);
        }
      })();
    }
  }, [initial, draftToken, ensureSDK, persistState, attachTopicSearchStatusSub, attachGenerationStatusSub]);

  const openEditor = React.useCallback(async () => {
    const sdk = await ensureSDK();
    if (!sdk) return;
    const videoId = state.videoId;
    analytics.track(EVENTS.WAYMARK_EDITOR_OPENED, {
      ms_since_generation_started: generationStartedAtRef.current
        ? Date.now() - generationStartedAtRef.current
        : null,
    });
    setIsEditorOpen(true);
    try {
      if (videoId) {
        await sdk.openEditorForVideo(videoId);
      } else {
        await sdk.openWaymark({ sourceUrl: state.sourceUrl ?? undefined });
      }
    } catch (e) {
      setIsEditorOpen(false);

      console.warn('[waymark] openEditor failed', e);
    }
  }, [ensureSDK, state.videoId, state.sourceUrl]);

  const closeEditor = React.useCallback(async () => {
    const sdk = sdkRef.current;
    if (!sdk) {
      setIsEditorOpen(false);
      return;
    }
    try {
      await sdk.close();
    } catch (e) {

      console.warn('[waymark] close failed', e);
    } finally {
      setIsEditorOpen(false);
    }
  }, []);

  // Clean up the SDK on unmount — the provider lives for the whole wizard
  // session, so this only fires on logical teardown (user navigates away
  // from the slug entirely).
  React.useEffect(() => {
    return () => {
      const sdk = sdkRef.current;
      if (!sdk) return;
      sdkRef.current = null;
      void sdk.cleanup().catch(() => { /* noop */ });
    };
  }, []);

  const value: WaymarkContextValue = {
    status: state.status,
    sourceUrl: state.sourceUrl,
    videoId: state.videoId,
    renderedUrl: state.renderedUrl,
    errorMessage: state.errorMessage,
    isFinalizing,
    isHydrating,
    startGenerationForUrl,
    openEditor,
    closeEditor,
    isEditorOpen,
  };

  return (
    <WaymarkContext.Provider value={value}>
      {children}
      {/*
        The SDK iframe lives here for the whole wizard session — DOM-stable
        across step navigation so background topic/video generation polling
        survives. When closed, the container is 1×1 off-screen. When open,
        it renders as a centered window (~90% viewport) over a dimmed
        backdrop, NOT a fullscreen takeover.
      */}
      {isEditorOpen && (
        <div
          aria-hidden="true"
          onClick={() => { void closeEditor(); }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            zIndex: 999,
          }}
        />
      )}
      <div
        ref={containerRef}
        data-waymark-container
        style={
          isEditorOpen
            ? {
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 'min(1100px, 92vw)',
                height: 'min(720px, 88vh)',
                borderRadius: 12,
                overflow: 'hidden',
                boxShadow: '0 25px 60px -15px rgba(15, 23, 42, 0.45)',
                background: '#fff',
                zIndex: 1000,
                pointerEvents: 'auto',
              }
            : {
                position: 'absolute',
                left: -99999,
                top: -99999,
                width: 1,
                height: 1,
                zIndex: -1,
                pointerEvents: 'none',
              }
        }
      />
    </WaymarkContext.Provider>
  );
};
